import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  forwardRef,
  ViewChild,
  ElementRef,
  CUSTOM_ELEMENTS_SCHEMA,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import * as _ from 'lodash'
import Swiper from 'swiper/swiper-bundle';

export interface GridOption {
  id: string | number;
  label: string;

  /**
   * SVG como URL o dataURL:
   * - URL: "/api/icons/123" (Content-Type: image/svg+xml)
   * - dataURL: "data:image/svg+xml;base64,...."  (ideal si viene de bytea)
   */
  image?: string;
}

@Component({
  selector: 'app-image-grid-selector',
  templateUrl: './image-grid-selector.component.html',
  styleUrls: ['./image-grid-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageGridSelectorComponent),
      multi: true,
    },
  ],
})
export class ImageGridSelectorComponent implements ControlValueAccessor, OnChanges, AfterViewInit, OnDestroy {
  @Input() options: GridOption[] = [];
  @Input() readonly = false;

  /** grid = tu modo anterior, row = carrusel 1 fila */
  @Input() mode: 'grid' | 'row' = 'grid';
  @Input() itemSize: 'normal' | 'large' = 'normal';

  @ViewChild('swiperEl', {static: false}) swiperEl?: ElementRef<any>;
swiper?: Swiper;
private swiperInitTimer?: any;
  value?: string | number;
  activeIndex = 0;

  paginationConfig = {
    el: '.external-pagination',
    clickable: true,
  };
private pendingSlideTo: number | null = null;

  onChange = (_: any) => {};
  onTouched = () => {};

  // Cache para pages grid
  lastPages: GridOption[][] = [];
  lastOptions?: GridOption[];

  ngOnChanges(changes: SimpleChanges): void {
     if (changes['options'] || changes['mode']) {
      this.tryAutoSelectSingleOption();
      this.scheduleInitSwiper();     // 👈 re-inicializa si cambió el DOM
      this.syncSwiperToValue();
    }
  }
  ngAfterViewInit() {
    this.scheduleInitSwiper();
  }
 ngOnDestroy() {
    this.destroySwiper();
    if (this.swiperInitTimer) clearTimeout(this.swiperInitTimer);
  }

   private scheduleInitSwiper() {
    if (this.swiperInitTimer) clearTimeout(this.swiperInitTimer);

    // esperamos a que Angular pinte el template del *ngIf y el *ngFor
    this.swiperInitTimer = setTimeout(() => this.initSwiper(), 0);
  }

private initSwiper() {
  if (!_.get(this.swiperEl, 'nativeElement')) return;

  this.destroySwiper();

  this.swiper = new Swiper(this.swiperEl.nativeElement, {
    speed: 450,
    slidesPerView: 3,
    slidesPerGroup: 3,      // ✅ pagina de 2 en 2
    spaceBetween: 12,
    centeredSlides: false,
    watchOverflow: true,          // ✅ si no alcanza para paginar, oculta
    on: {
      slideChange: () => this.activeIndex = this.getActiveIdx()
    }
  });

  // ✅ ejecutar slide pendiente si existía
  if (this.pendingSlideTo !== null) {
    const idx = this.pendingSlideTo;
    this.pendingSlideTo = null;
    this.swiper.slideTo(idx);
  }

  // y sincroniza value
  this.syncSwiperToValue();
}


  private destroySwiper() {
    try {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = undefined;
      }
    } catch (err) { console.error(err); }
  }
  /* ========== ControlValueAccessor ========== */

  writeValue(val: string | number): void {
    this.value = val;
    this.syncSwiperToValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.readonly = isDisabled;
  }

  /* ========== Lógica ========== */

  select(option: GridOption) {
    if (this.readonly) return;

    this.value = option.id;
    this.onChange(option.id);
    this.onTouched();
  }

  isSelected(option: GridOption): boolean {
    return this.value === option.id;
  }

  /** Pages para modo grid (9 por página) */
  get pages(): GridOption[][] {
    if (this.lastOptions === this.options) return this.lastPages;

    const pages: GridOption[][] = [];
    for (let i = 0; i < (_.get(this.options, 'length', 0)); i += 9) {
      pages.push(this.options.slice(i, i + 9));
    }
    this.lastPages = pages;
    this.lastOptions = this.options;
    return pages;
  }

  /** Swiper change */
  onSlideChange(ev: any) {
    this.activeIndex = this.getActiveIdx()
  }

  prev() {
  try { this.swiper.slidePrev(); } catch (err) { console.error(err); }
}

next() {
  try { this.swiper.slideNext(); } catch (err) { console.error(err); }
}


slideTo(idx: number) {
  // si todavía no está listo, lo guardamos
  if (!this.swiper) {
    this.pendingSlideTo = idx;
    return;
  }

  try {
    this.swiper.slideTo(idx);
  } catch (err) {
    console.error(err);
  }
}


getActiveIdx() {
  return _.get(this.swiper, 'activeIndex', 0);
}

  /** Dots aproximados (modo row) */
  get dotsArray() {
    const perView = 3; // aproximación visual
    const pages = Math.max(1, Math.ceil((_.get(this.options, 'length', 0)) / perView));
    return Array.from({ length: pages });
  }

  /** Auto-select cuando hay 1 opción */
private tryAutoSelectSingleOption() {
  if (this.readonly) return;
  if (!this.options || this.options.length !== 1) return;

  const only = this.options[0];
  if (this.value === only.id) return;

  this.value = only.id;
  this.onChange(only.id);
  this.onTouched();

  // ✅ en vez de llamar a slideTo directo:
  this.pendingSlideTo = 0;
}


  /** Sincroniza el swiper con el value actual */
  private syncSwiperToValue() {
    if (!this.options || this.options.length === 0) return;

    // Modo row: slideTo(index del item)
    if (this.mode === 'row') {
      const idx = this.options.findIndex(o => o.id === this.value);
      if (idx >= 0) {
        setTimeout(() => {
          this.slideTo(idx);
          this.activeIndex = this.getActiveIdx();
        });
      } else {
        // si no hay value, deja en 0
        setTimeout(() => {
          this.slideTo(0)
          this.activeIndex = 0;
        });
      }
      return;
    }

    // Modo grid con pages de 9
    const idx = this.options.findIndex(o => o.id === this.value);
    if (idx >= 0 && this.options.length > 9) {
      const pageIndex = Math.floor(idx / 9);
      this.activeIndex = pageIndex;
      setTimeout(() => {
        this.slideTo(pageIndex)
      });
    }
  }
  getMaskStyle(img: string) {
  const url = `url("${img}")`;
  return {
    '-webkit-mask-image': url,
    'mask-image': url,
    '-webkit-mask-repeat': 'no-repeat',
    'mask-repeat': 'no-repeat',
    '-webkit-mask-position': 'center',
    'mask-position': 'center',
    '-webkit-mask-size': 'contain',
    'mask-size': 'contain',
  };
}
}
